import type { ReactNode } from 'react';
import { ChevronRight, Plus, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InlineNotice } from '@/components/medic/PatientCardStates';
import type { PatientRecordCollection } from '@/components/medic/patientCardRecordsData';

interface Props {
    title: string;
    description: string;
    collection: PatientRecordCollection;
    createLabel: string;
    onCreate: () => void;
    onOpen: (id: string) => void;
    onReload: () => void;
    /** Заголовок и пояснение пустого раздела (по смыслу раздела). */
    emptyTitle?: string;
    emptyText?: string;
    /** Дополнительные действия раздела (например, импорт анализов CSV). */
    actions?: ReactNode;
}

function statusTone(code: string): string {
    const value = String(code || '').toLowerCase();
    if (['active', 'final', 'completed', 'done', 'issued', 'accepted'].includes(value)) {
        return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400';
    }
    if (['draft', 'preliminary', 'provisional', 'planned'].includes(value)) {
        return 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400';
    }
    if (['cancelled', 'ruled_out', 'stopped', 'resolved'].includes(value)) {
        return 'border-border bg-muted text-muted-foreground';
    }
    return 'border-border bg-muted text-muted-foreground';
}

export function PatientCardRecordsSection({
    title,
    description,
    collection,
    createLabel,
    onCreate,
    onOpen,
    onReload,
    emptyTitle,
    emptyText,
    actions,
}: Props) {
    const count = collection.state === 'ready' ? collection.items.length : null;
    return (
        <section className="space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium">{title}</h3>
                        {count !== null && (
                            <span className="rounded-full border bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
                                {count}
                            </span>
                        )}
                    </div>
                    <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                    {actions}
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-8"
                        onClick={onReload}
                        aria-label="Обновить раздел"
                        title="Обновить раздел"
                    >
                        <RefreshCw className="h-3.5 w-3.5 sm:mr-1" />
                        <span className="hidden sm:inline">Обновить</span>
                    </Button>
                    <Button size="sm" className="h-8" onClick={onCreate}>
                        <Plus className="mr-1 h-3.5 w-3.5" /> {createLabel}
                    </Button>
                </div>
            </div>

            {collection.state !== 'ready' ? (
                <InlineNotice
                    text={collection.error || collection.note || 'Раздел недоступен'}
                    tone={collection.state === 'denied' || collection.state === 'error' ? 'danger' : 'warning'}
                    onRetry={collection.state === 'error' ? onReload : undefined}
                />
            ) : collection.items.length === 0 ? (
                <div className="rounded-lg border border-dashed bg-card px-4 py-8 text-center">
                    <div className="text-sm font-medium">{emptyTitle || 'Пока нет записей'}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                        {emptyText || 'Добавьте первую запись — она сразу появится в карточке пациента.'}
                    </div>
                    <Button size="sm" className="mt-3" onClick={onCreate}>
                        <Plus className="mr-1 h-3.5 w-3.5" /> {createLabel}
                    </Button>
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg border bg-card">
                    {collection.items.map((item, index) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={() => onOpen(item.id)}
                            className={`flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors hover:bg-muted/50 ${
                                index > 0 ? 'border-t' : ''
                            }`}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="break-words text-sm font-medium">{item.title}</span>
                                    {item.statusLabel && (
                                        <span className={`rounded-full border px-2 py-0.5 text-[10px] ${statusTone(item.statusCode)}`}>
                                            {item.statusLabel}
                                        </span>
                                    )}
                                </div>
                                {(item.subtitle || item.date) && (
                                    <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                                        {item.subtitle && <span className="break-words">{item.subtitle}</span>}
                                        {item.date && <span>{item.date}</span>}
                                    </div>
                                )}
                            </div>
                            <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                        </button>
                    ))}
                </div>
            )}
        </section>
    );
}
